import { Dialog, DialogContent } from "@breakout/ui/components/Dialog"
import { Shield } from "lucide-react"
import { useState } from "react"

const STORAGE_KEY = "breakout_region_confirmed"

/**
 * Check localStorage synchronously to determine initial open state.
 * This prevents flash of content before the modal appears.
 */
function getInitialOpenState(): boolean {
	if (typeof window === "undefined") {
		return false
	}
	return !localStorage.getItem(STORAGE_KEY)
}

export function RegionalRestrictionModal() {
	const [isOpen, setIsOpen] = useState(getInitialOpenState)

	const handleUSResident = () => {
		window.location.href = "https://x.com/BreakoutApp"
	}

	const handleNotUSResident = () => {
		localStorage.setItem(STORAGE_KEY, "true")
		setIsOpen(false)
	}

	// Only allow closing via explicit button click, never via escape/outside click
	const handleOpenChange = (open: boolean) => {
		// Prevent any programmatic close - user must click the confirmation button
		if (open) {
			setIsOpen(true)
		}
		// Ignore close attempts - modal can only be closed via handleNotUSResident
	}

	return (
		<Dialog modal onOpenChange={handleOpenChange} open={isOpen}>
			<DialogContent
				className="rounded-3xl p-0"
				hideCloseButton
				onEscapeKeyDown={e => e.preventDefault()}
				onInteractOutside={e => e.preventDefault()}
				onPointerDownOutside={e => e.preventDefault()}
				overlayClassName="bg-black/60 backdrop-blur-sm"
			>
				{/* Decorative top accent */}
				<div className="absolute inset-x-0 top-0 h-1 bg-breakout-brand opacity-50" />

				<div className="flex flex-col items-center p-8 text-center sm:p-10">
					{/* Icon */}
					<div className="mb-6 flex size-20 items-center justify-center rounded-full bg-bg-tertiary shadow-inner ring-1 ring-border-subtle">
						<Shield className="size-10 text-breakout-secondary" />
					</div>

					<h2 className="mb-3 text-balance font-bold font-display text-2xl text-text-primary">
						Regional Restricted Access
					</h2>

					<p className="mb-8 text-text-secondary">
						Breakout is not available to US residents or persons in restricted jurisdictions. By
						continuing, you confirm you are not a US person and agree to our{" "}
						<a
							className="font-medium text-breakout-brand underline decoration-breakout-brand/30 underline-offset-4 hover:decoration-breakout-brand"
							href="https://showtime-xyz.notion.site/Legal-Public-c407e36eb7cd414ca190245ca8621e68"
							rel="noopener noreferrer"
							target="_blank"
						>
							Terms of Service
						</a>
						.
					</p>

					<div className="flex w-full flex-col gap-3">
						<button
							className="w-full rounded-xl bg-breakout-brand px-4 py-3.5 font-bold text-white shadow-md transition-all hover:bg-breakout-brand-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-breakout-brand focus-visible:ring-offset-2 active:scale-[0.98]"
							onClick={handleNotUSResident}
							type="button"
						>
							I am NOT a US Resident
						</button>

						<button
							className="w-full rounded-xl border border-border bg-bg-tertiary px-4 py-3.5 font-medium text-text-secondary transition-all hover:bg-bg-secondary hover:text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-highlight focus-visible:ring-offset-2 active:scale-[0.98]"
							onClick={handleUSResident}
							type="button"
						>
							Exit to X
						</button>
					</div>
				</div>
			</DialogContent>
		</Dialog>
	)
}
